import React, { useState } from 'react';

const ChallengeFriendsEntry = ({ onStartQuizGame, onBack, currentUser }) => {
  const [quizCode, setQuizCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showSignUpModal, setShowSignUpModal] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!quizCode.trim()) {
      setError('Please enter a quiz code');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Validate the quiz code by making a test request
      const response = await fetch(`/api/quiz/question?quiz_code=${encodeURIComponent(quizCode.trim())}`);
      const data = await response.json();

      if (data.success || (data.quiz_context && data.quiz_context.quiz_mode)) {
        // Quiz code is valid, start the game
        onStartQuizGame(quizCode.trim().toUpperCase());
      } else {
        setError('Quiz code not found. Please check the code and try again.');
      }
    } catch (error) {
      console.error('Error validating quiz code:', error);
      setError('Failed to validate quiz code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCodeChange = (e) => {
    const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').substring(0, 6);
    setQuizCode(value);
    setError('');
  };

  const handleSignUp = () => {
    window.location.href = '/users/sign_up';
  };

  const handleLogin = () => {
    window.location.href = '/users/sign_in';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-600 via-pink-600 to-red-500 flex items-center justify-center p-4">
      <div className="max-w-4xl w-full">
        {/* Back button */}
        <button
          onClick={onBack}
          className="mb-6 text-white/80 hover:text-white flex items-center gap-2 transition-colors"
          disabled={loading}
        >
          ← Back to Game Modes
        </button>

        <div className="grid lg:grid-cols-2 gap-8 items-center">
          {/* Left side - Marketing content */}
          <div className="text-white space-y-6">
            <div className="text-center lg:text-left">
              <div className="text-7xl mb-4">🚀</div>
              <h1 className="text-4xl lg:text-5xl font-bold mb-4 leading-tight">
                Your friend challenged you!
              </h1>
              <p className="text-xl text-white/90 mb-6">
                They've created a custom quiz just for you. Enter the code they shared to start the Brain Climb challenge!
              </p>
            </div>

            {/* Benefits for signed-up users */}
            {currentUser ? (
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                  <span>🎮</span>
                  Welcome back, {currentUser.name}!
                </h3>
                <div className="space-y-3 text-white/90">
                  <div className="flex items-start gap-3">
                    <span className="text-yellow-300">🪙</span>
                    <span>Earn coins for correct answers</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-green-300">🏆</span>
                    <span>Track your progress and achievements</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-blue-300">📊</span>
                    <span>See detailed results after the quiz</span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                  <span>✨</span>
                  Want to create your own quizzes?
                </h3>
                <div className="space-y-3 text-white/90 mb-4">
                  <div className="flex items-start gap-3">
                    <span className="text-yellow-300">🧠</span>
                    <span>Create unlimited custom quizzes with AI</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-green-300">🎯</span>
                    <span>Challenge friends and track their progress</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-blue-300">🪙</span>
                    <span>Earn coins and unlock rewards</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-purple-300">📚</span>
                    <span>Access thousands of educational games</span>
                  </div>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={handleSignUp}
                    className="flex-1 bg-white text-purple-600 font-bold py-3 px-4 rounded-lg hover:bg-gray-100 transition-all transform hover:scale-105"
                  >
                    Sign Up Free
                  </button>
                  <button
                    onClick={handleLogin}
                    className="flex-1 bg-white/20 text-white font-medium py-3 px-4 rounded-lg hover:bg-white/30 transition-all border border-white/30"
                  >
                    Login
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Right side - Quiz entry form */}
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 shadow-2xl border border-white/20">
            <div className="text-center mb-8">
              <div className="text-6xl mb-4">🎯</div>
              <h2 className="text-2xl font-bold text-white mb-2">Enter Challenge Code</h2>
              <p className="text-white/80">Ready to prove your skills?</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="quizCode" className="block text-white font-medium mb-2">
                  Friend's Quiz Code
                </label>
                <input
                  type="text"
                  id="quizCode"
                  value={quizCode}
                  onChange={handleCodeChange}
                  placeholder="ABC123"
                  className="w-full px-4 py-3 rounded-lg bg-white/20 border border-white/30 text-white placeholder-white/60 text-center text-2xl font-mono tracking-wider focus:ring-2 focus:ring-white/50 focus:border-white/50 outline-none transition-all"
                  disabled={loading}
                  maxLength={6}
                  autoComplete="off"
                  autoFocus
                />
                {error && (
                  <p className="text-red-200 text-sm mt-2 bg-red-500/20 rounded-lg p-2">
                    {error}
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={!quizCode.trim() || loading}
                className="w-full bg-gradient-to-r from-yellow-400 to-orange-500 text-white font-bold py-3 px-6 rounded-lg hover:from-yellow-500 hover:to-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all transform hover:scale-105 active:scale-95 shadow-lg"
              >
                {loading ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                    Starting Challenge...
                  </div>
                ) : (
                  <div className="flex items-center justify-center gap-2">
                    <span>🚀</span>
                    <span>Accept Challenge</span>
                  </div>
                )}
              </button>
            </form>

            {/* Help section */}
            <div className="mt-6 pt-6 border-t border-white/20">
              <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                <span>💡</span>
                How it works:
              </h4>
              <ul className="text-white/80 text-sm space-y-2">
                <li>• Enter the 6-character code your friend shared</li>
                <li>• Play the jumping game with their custom questions</li>
                <li>• Show off your knowledge and climbing skills!</li>
                {!currentUser && (
                  <li>• <span className="text-yellow-300">Sign up to save your progress & earn rewards</span></li>
                )}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChallengeFriendsEntry;